import React, { useEffect, useMemo, useState } from "react";
import HeaderCustom from "../../../components/HeaderCustom";
import {
  Alert,
  Autocomplete,
  Backdrop,
  CircularProgress,
  Snackbar,
  TextField,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  getDetalleBeneficio,
  getFiltrarLocales,
  postCrearBeneficio,
  postUpdateBeneficio,
} from "../../../api/ApiService";
import { useSelector } from "react-redux";

function BeneficioAddDetailsPage() {
  const [loadingInicial, setLoadingInicial] = useState(true);
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("");
  const [mensaje, setMensaje] = useState("");

  const [nombre, setNombre] = useState("");
  const [orden, setOrden] = useState("");
  const [localesOptions, setLocalesOptions] = useState([]);
  const [localesSelected, setLocalesSelected] = useState([]);
  const [localDescriptions, setLocalDescriptions] = useState({});

  const stateToken = useSelector((state) => state.reducerGlobal.token);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const isViewMode = mode === "view";

  const tituloPantalla = useMemo(() => {
    if (isViewMode) return "Detalle de Beneficio";
    return id ? "Editar Beneficio" : "Nuevo Beneficio";
  }, [id, isViewMode]);

  // Cierra el snackbar de feedback.
  const handleCloseSnackbar = () => {
    setOpen(false);
  };

  // Muestra mensajes uniformes de exito, warning o error.
  const showFeedback = (type, text) => {
    setSeverity(type);
    setMensaje(text);
    setOpen(true);
  };

  // Regresa a la pantalla anterior (lista o detalle previo).
  const handleCancelar = () => {
    navigate(-1);
  };

  // Convierte un local en etiqueta amigable para el selector.
  const getLocalLabel = (local) => {
    if (!local) return "";
    if (typeof local === "string") return local;
    return local?.nombre_fantasia || local?.razon_social || local?.ruc || "";
  };

  // Carga todos los locales disponibles para el autocomplete multiple.
  const handleCargarLocales = () => {
    const body = {
      page: 1,
      per_page: 9999,
      estado: "activo",
      nombre: "",
      categoriaId: ""
    };
    return getFiltrarLocales(stateToken?.access_token, body)
      .then((response) => {
        const locales = response?.data || [];
        setLocalesOptions(locales);
        return locales;
      })
      .catch((error) => {
        console.log("error-getFiltrarLocales", error);
        setLocalesOptions([]);
        return [];
      });
  };

  // Carga el detalle del beneficio cuando la pantalla esta en editar/ver.
  const handleCargarDetalle = () => {
    return getDetalleBeneficio(stateToken?.access_token, id)
      .then((response) => {
        const detalle = response?.data || response || {};
        setNombre(detalle?.nombre || "");
        setOrden(detalle?.orden ? String(detalle?.orden) : "");

        const localesDelDetalle = Array.isArray(detalle?.locales) ? detalle.locales : [];
        
        // Filtrar solo locales activos (estado !== "eliminados")
        const localesActivos = localesDelDetalle.filter(local => {
          // Si el local no tiene estado o está activo, lo incluimos
          // Excluimos solo si estado es explícitamente "eliminados" o deleted_at existe
          return !local?.deleted_at && local?.estado !== "eliminados";
        });
        
        setLocalesSelected(localesActivos);

        const descriptions = {};
        localesActivos.forEach((local) => {
          descriptions[local.id] = local?.pivot?.observacion || "";
        });
        setLocalDescriptions(descriptions);
      })
      .catch((error) => {
        console.log("error-getDetalleBeneficio", error);
        showFeedback("error", "Error al cargar el detalle del beneficio");
      });
  };

  // Construye el payload requerido por crear/actualizar beneficio.
  const buildBody = () => {
    return {
      nombre: nombre.trim(),
      orden: Number(orden) || 0,
      locales: localesSelected.map((local) => ({
        local_id: local.id,
        observacion: (localDescriptions[local.id] || "").trim(),
      })),
    };
  };

  // Valida campos obligatorios antes de enviar a backend.
  const validarFormulario = () => {
    if (!nombre.trim()) {
      showFeedback("warning", "El campo nombre es obligatorio");
      return false;
    }

    if (!orden || Number.isNaN(Number(orden))) {
      showFeedback("warning", "El campo orden es obligatorio y debe ser numerico");
      return false;
    }

    if (localesSelected.length === 0) {
      showFeedback("warning", "Debe seleccionar al menos un local");
      return false;
    }

    return true;
  };

  // Crea un beneficio nuevo y vuelve al listado al finalizar.
  const handleCrearBeneficio = (body) => {
    postCrearBeneficio(stateToken?.access_token, body)
      .then(() => {
        setLoadingGuardar(false);
        showFeedback("success", "Beneficio creado con exito");
        setTimeout(() => navigate('/app/admin/beneficios'), 1000);
        //limpiar formulario para agregar otro beneficio
        setNombre("");
        setOrden("");
        setLocalesSelected([]);
        setLocalDescriptions({});
      })
      .catch((error) => {
        console.log("error-postCrearBeneficio", error);
        setLoadingGuardar(false);
        showFeedback("error", "Error al crear el beneficio");
      });
  };

  const handleLocalDescriptionChange = (localId, value) => {
    setLocalDescriptions((prev) => ({ ...prev, [localId]: value }));
  };

  // Sincroniza descripiones al agregar/quitar locales del selector.
  const handleLocalesChange = (event, value) => {
    setLocalesSelected(value);
    setLocalDescriptions((prev) => {
      const next = {};
      value.forEach((local) => {
        next[local.id] = prev[local.id] ?? "";
      });
      return next;
    });
  };

  // Actualiza un beneficio existente y vuelve al listado al finalizar.
  const handleActualizarBeneficio = (body) => {
    postUpdateBeneficio(stateToken?.access_token, body, id)
      .then(() => {
        setLoadingGuardar(false);
        showFeedback("success", "Beneficio actualizado con exito");
        setTimeout(() => navigate('/app/admin/beneficios'), 1000);
      })
      .catch((error) => {
        console.log("error-postUpdateBeneficio", error);
        setLoadingGuardar(false);
        showFeedback("error", "Error al actualizar el beneficio");
      });
  };

  // Orquesta validacion + envio para crear o actualizar, segun corresponda.
  const handleGuardar = () => {
    if (!validarFormulario()) return;

    const body = buildBody();
    setLoadingGuardar(true);

    if (id) {
      handleActualizarBeneficio(body);
    } else {
      handleCrearBeneficio(body);
    }
  };

  // Carga inicial de catalogo de locales y detalle (si es edicion/ver).
  useEffect(() => {
    if (!stateToken?.access_token) return;

    let mounted = true;
    setLoadingInicial(true);

    Promise.all([handleCargarLocales(), id ? handleCargarDetalle() : Promise.resolve()]).finally(() => {
      if (mounted) setLoadingInicial(false);
    });

    return () => {
      mounted = false;
    };
  }, [id, stateToken?.access_token]);

  return (
    <HeaderCustom>
      <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6">
            <h1 className="mb-1 text-2xl font-bold text-gray-800">{tituloPantalla}</h1>
          </div>

          {loadingInicial ? (
            <div className="rounded-xl bg-white p-8 shadow-md">
              <div className="mb-6 h-6 w-1/3 animate-pulse rounded bg-gray-200" />
              <div className="space-y-4">
                <div className="h-10 animate-pulse rounded bg-gray-200" />
                <div className="h-10 animate-pulse rounded bg-gray-200" />
                <div className="h-10 animate-pulse rounded bg-gray-200" />
                <div className="h-24 animate-pulse rounded bg-gray-200" />
              </div>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl bg-white shadow-xl">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      disabled={isViewMode}
                      placeholder="Ej: Gratis un appetizer"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Orden</label>
                    <input
                      type="number"
                      value={orden}
                      onChange={(e) => setOrden(e.target.value)}
                      disabled={isViewMode}
                      placeholder="Ej: 50"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">Locales</label>
                    <Autocomplete
                      multiple
                      options={localesOptions}
                      value={localesSelected}
                      onChange={handleLocalesChange}
                      disabled={isViewMode}
                      getOptionLabel={getLocalLabel}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Seleccione uno o varios locales" size="small" />
                      )}
                      noOptionsText="No hay locales"
                    />
                  </div>

                  {localesSelected.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="mb-3 block text-sm font-medium text-gray-700">
                        Descripción por local
                      </label>
                      <div className="space-y-4">
                        {localesSelected.map((local) => (
                          <div key={local.id} className="rounded-lg border border-gray-200 p-4">
                            <p className="mb-2 text-sm font-semibold text-gray-800">
                              {local?.nombre_fantasia || local?.razon_social || local?.ruc}
                            </p>
                            <textarea
                              value={localDescriptions[local.id] || ""}
                              onChange={(e) => handleLocalDescriptionChange(local.id, e.target.value)}
                              disabled={isViewMode}
                              rows={2}
                              placeholder="Descripción del beneficio para este local"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-start gap-3 border-t border-gray-200 p-6">
                  {!isViewMode && (
                    <button
                      type="button"
                      disabled={loadingGuardar}
                      onClick={handleGuardar}
                      className="rounded-lg bg-[#104063] px-6 py-2 font-medium text-white transition-colors hover:bg-[#0d3553] disabled:opacity-70"
                    >
                      {loadingGuardar ? "Guardando..." : id ? "Guardar Cambios" : "Guardar"}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleCancelar}
                    className="rounded-lg bg-gray-100 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                  >
                    {isViewMode ? "Volver" : "Cancelar"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <Backdrop open={loadingGuardar} sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        autoHideDuration={2200}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={severity} elevation={6} variant="filled">
          {mensaje}
        </Alert>
      </Snackbar>
    </HeaderCustom>
  );
}

export default BeneficioAddDetailsPage;
